import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT, COURIER_CODE_GROUP } from '~/constants';

import { receiveKeys } from './keys';

import type { TypeGetReturnDisposalCodesResponse, TypeReturnDisposalCodeApi } from '~/types/receive.types';

/**
 * [입고관리 > 회송/폐기 관리] 택배사 코드 조회 API (정렬은 서버 응답에 위임)
 *
 * @note 렌탈사와 동일한 `GET /codes` 엔드포인트를 codeGroup 으로 구분한다.
 *       MSW mock 은 경로만 매칭하므로 별도 등록하지 않고 렌탈사 codes mock 시나리오를 공유한다.
 *
 *       `codeSubGroup` 을 함께 보내야 회수 신청에 쓸 수 있는 택배사만 내려온다. codeGroup 만
 *       보내면 신청 API 가 받지 않는 레거시 코드(`CJDT`·`LTT`)까지 붙어 오고, 그중 `LTT` 는
 *       유효한 `HYUNDAI` 와 이름이 똑같이 "롯데택배" 라서 목록에서 앞선 쪽을 고르면
 *       신청이 400(Invalid request content) 으로 실패한다.
 */
const fetchReturnDisposalCouriers = async (): Promise<TypeReturnDisposalCodeApi[]> => {
  const { data } = await axiosInstance.get<TypeGetReturnDisposalCodesResponse>(
    API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_CODES,
    { params: { codeGroup: COURIER_CODE_GROUP, codeSubGroup: COURIER_CODE_GROUP } }
  );

  return data.data.codes;
};

type TypeUseGetReturnDisposalCouriersQueryParams = {
  enabled?: boolean;
};

/** [입고관리 > 회송/폐기 관리] 택배사 코드 조회 Query Hook */
export const useGetReturnDisposalCouriersQuery = ({
  enabled = true,
}: TypeUseGetReturnDisposalCouriersQueryParams = {}) => {
  return useQuery({
    queryKey: receiveKeys.returnDisposalCodes(COURIER_CODE_GROUP),
    queryFn: fetchReturnDisposalCouriers,
    enabled,
    staleTime: 1000 * 60 * 5,
  });
};
